<template>
  <div class="tw-w-full">
    <div class="tw-rounded-xl tw-border tw-border-slate-200/70 tw-bg-white/60 tw-backdrop-blur tw-shadow-sm tw-p-3">
      <div class="tw-flex tw-flex-col lg:tw-flex-row lg:tw-items-center tw-gap-3">

        <div class="tw-flex-1">
          <SearchInput v-model="searchTerm" placeholder="Container, BL or Customer..." class="tw-w-full" />
        </div>

        <div v-if="!isSearchingInput" class="tw-flex tw-flex-nowrap tw-gap-2 tw-whitespace-nowrap tw-justify-end">
          <button v-for="filter in ['In Progress', 'Assigned (Pre-dispatch)', 'Available', 'Delivered', 'All']"
            :key="filter" type="button"
            class="tw-inline-flex tw-items-center tw-gap-2 tw-rounded-full tw-border tw-px-3 tw-py-2 tw-text-sm tw-font-medium tw-transition"
            :class="activeFilter === filter
              ? 'tw-border-transparent tw-bg-slate-900 tw-text-white'
              : 'tw-border-slate-200 tw-bg-white/60 tw-text-slate-700 hover:tw-bg-white'" @click="setFilter(filter)"
            :disabled="isListLoading">
            <span>{{ filter }}</span>
            <span v-if="activeFilter === filter" class="tw-opacity-60">{{ totalCount }}</span>
          </button>
        </div>
      </div>

      <div
        class="tw-mt-2 tw-flex tw-items-center tw-justify-between tw-text-[11px] tw-text-slate-500 tw-pt-2 tw-border-t tw-border-slate-100">
        <div class="tw-flex tw-items-center tw-gap-3">
          <template v-if="isSearchingInput">
            <span>Results: <strong>{{ displayedContainers.length }}</strong></span>
          </template>
          <template v-else>
            <span>Showing {{ displayedContainers.length }} of {{ totalCount }}</span>
            <span class="tw-text-slate-300">|</span>
            <span v-if="pageCount > 1">Page {{ pageNum + 1 }} of {{ pageCount }}</span>
          </template>
        </div>

        <div class="tw-flex tw-items-center tw-gap-3">
          <span v-if="isListLoading" class="tw-italic">Loading…</span>
          <label class="tw-flex tw-items-center tw-gap-2">
            <span>Rows</span>
            <select class="tw-rounded tw-border-slate-200 tw-bg-white tw-px-1 tw-py-0.5" v-model.number="pageSize">
              <option v-for="size in [10, 25, 50, 100]" :key="size" :value="size">{{ size }}</option>
            </select>
          </label>
        </div>
      </div>

      <Modal v-model:isOpen="isDispatchContainerOpen" modalId="dispatch-container-view-modal" maxWidth="7xl" :hasCloseButton="false" @close="closeDispatchContainer">
        <template #content>
          <div class="tw--m-5">
            <DispatchContainerModal :containerId="showTripForContainerIdx"
              @dispachContainerRefetch="updateDispatchContainers" :drivers="drivers" :filteredChassis="chassis"
              @close="closeDispatchContainer"></DispatchContainerModal>
          </div>
        </template>
      </Modal>
      <div class="tw-mt-4 tw-text-sm tw-text-slate-700">
        <ListPage variant="card" :loading="isListLoading" :cardPadding="false">
          <div v-if="!isSearchingInput">
            <DispatchTable @toggleTripStatus="toggleTripStatus" @updateSortBy="updateSortBy"
              :containers="displayedContainers" :sortBy="sortBy" :sortOrder="sortOrder"></DispatchTable>
          </div>

          <div v-if="isSearchingInput" class="tw-p-4">
            <div v-if="isServerSearching" class="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-py-6">
              <font-awesome-icon icon="circle-notch" spin class="tw-h-4 tw-w-4 tw-text-slate-500" />
              <span class="tw-text-sm tw-text-slate-600">Searching on server...</span>
            </div>
            <div class="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-slate-600">
              <span>Results:</span>
              <span
                class="tw-inline-flex tw-min-w-[28px] tw-justify-center tw-rounded-full tw-bg-slate-900/5 tw-px-2 tw-py-0.5 tw-text-xs tw-text-slate-700">{{
                  displayedContainers.length }}</span>
            </div>
          </div>

          <div v-if="isSearchingInput">
            <DispatchTable @toggleTripStatus="toggleTripStatus" @updateSortBy="updateSortBy"
              :containers="displayedContainers" :sortBy="sortBy" :sortOrder="sortOrder"></DispatchTable>
          </div>
        </ListPage>

        <div v-if="!isSearching" class="tw-mt-4 tw-flex tw-justify-center">
          <div class="tw-flex tw-items-center tw-gap-2">
            <button type="button"
              class="tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white/60 tw-backdrop-blur tw-px-3 tw-py-2 tw-text-sm tw-text-slate-700 hover:tw-bg-white disabled:tw-opacity-60 disabled:tw-cursor-not-allowed"
              @click="prevPage" :disabled="pageNum === 0 || isListLoading">
              Prev
            </button>

            <div class="tw-flex tw-items-center tw-gap-1">
              <template v-for="(p, index) in visiblePages">
                <span v-if="typeof p === 'string'" :key="`ellipsis-${index}`"
                  class="tw-px-2 tw-text-sm tw-text-slate-500">
                  ...
                </span>
                <button v-else :key="`page-${p}`" type="button"
                  class="tw-rounded-lg tw-border tw-px-3 tw-py-2 tw-text-sm tw-transition" :class="p - 1 === pageNum
                    ? 'tw-border-transparent tw-bg-slate-900 tw-text-white'
                    : 'tw-border-slate-200 tw-bg-white/60 tw-backdrop-blur tw-text-slate-700 hover:tw-bg-white'"
                  @click="goToPage(p - 1)" :disabled="isListLoading">
                  {{ p }}
                </button>
              </template>
              <span class="tw-ml-2 tw-text-sm tw-text-slate-500">of {{ pageCount }}</span>
            </div>

            <button type="button"
              class="tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white/60 tw-backdrop-blur tw-px-3 tw-py-2 tw-text-sm tw-text-slate-700 hover:tw-bg-white disabled:tw-opacity-60 disabled:tw-cursor-not-allowed"
              @click="nextPage" :disabled="pageNum + 1 >= pageCount || isListLoading">
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';

import ListPage from '@/components/core/ListPage.vue';
import SearchInput from '../core/SearchInput.vue';
import DispatchContainerModal from './DispatchContainerModal.vue';
import DispatchTable from './DispatchTable.vue';
import Modal from '@/components/core/Modal/Modal.vue';

import DispatchContainers from '../../gql/dispatch/DispatchContainers.gql';
import DriversQuery from '../../gql/drivers/Drivers.gql';
import ChassisQuery from '../../gql/orders/Chassis.gql';

import { TRIP_STATUS } from '../../types';

export default defineComponent({
  components: {
    ListPage,
    SearchInput,
    DispatchContainerModal,
    DispatchTable,
    Modal,
  },
  data() {
    return {
      activeFilter: 'In Progress',
      tripAdditionContainer: undefined,
      showTripForContainerIdx: -1,
      searchTerm: '',
      debouncedSearchTerm: '',
      pageNum: 0,
      pageSize: 25,
      totalCount: 0,
      drivers: [] as any[],
      chassis: [] as any[],
      sortBy: 'containerId',
      sortOrder: 'ASC',
      editingTrip: {
        id: undefined,
        afterLegId: undefined,
        type: undefined,
        origin: undefined,
        destination: undefined,
        driver: undefined,
        chassis: undefined,
        isEditingDriver: false,
        isEditingChassis: false,
        isEditingDestination: false,
      },
      debounce: null as any,
      serverSearchResults: [] as any[],
      isServerSearching: false,
    };
  },
  computed: {
    isSearchingInput(): boolean {
      return (this as any).searchTerm.length > 0;
    },
    isSearching(): boolean {
      return (this as any).debouncedSearchTerm.length > 0;
    },
    isDispatchContainerOpen: {
      get(): boolean {
        return (this as any).showTripForContainerIdx !== -1;
      },
      set(value: boolean) {
        if (!value) {
          (this as any).closeDispatchContainer();
        }
      },
    },
    isListLoading(): boolean {
      const vm = (this as any);
      const queryLoading = !!vm.$apollo?.queries?.dispatchContainers?.loading;
      return queryLoading || !!vm.isServerSearching;
    },
    displayedContainers(): any[] {
      return (this as any).dispatchContainers?.containers || [];
    },
    pageCount(): number {
      const vm = this as any;

      if (vm.totalCount > 0) {
        return Math.ceil(vm.totalCount / vm.pageSize);
      }
      return 1;
    },
    visiblePages(): (number | string)[] {
      const vm = this as any;

      const totalPages = vm.pageCount;
      const currentPage = vm.pageNum + 1;
      const maxPagesToShow = 7;

      if (totalPages <= maxPagesToShow) {
        return Array.from({ length: totalPages }, (_, i) => i + 1);
      }

      const pages: (number | string)[] = [];
      pages.push(1);

      let start = Math.max(2, currentPage - 1);
      let end = Math.min(totalPages - 1, currentPage + 1);

      if (currentPage <= 3) {
        start = 2;
        end = 4;
      } else if (currentPage > totalPages - 3) {
        start = totalPages - 3;
        end = totalPages - 1;
      }

      if (start > 2) pages.push('...');
      for (let i = start; i <= end; i++) pages.push(i);
      if (end < totalPages - 1) pages.push('...');

      pages.push(totalPages);
      return pages;
    },
  },
  watch: {
    searchTerm(newValue: string) {
      const vm = this as any;
      clearTimeout(vm.debounce);

      vm.debounce = setTimeout(() => {
        vm.debouncedSearchTerm = newValue;
        vm.pageNum = 0;
      }, 350);
    },
    pageSize() {
      const vm = this as any;
      if (vm.pageNum !== 0) {
        vm.pageNum = 0;
      }
    },
  },
  apollo: {
    dispatchContainers: {
      query: DispatchContainers,
      variables() {
        return {
          pageNum: (this as any).pageNum,
          pageSize: (this as any).pageSize,
          statusFilter: (this as any).activeFilter,
          searchTerm: (this as any).debouncedSearchTerm,
          sortBy: (this as any).sortBy,
          sortOrder: (this as any).sortOrder,
        };
      },
      result(result) {
        const data = result?.data;
        const o = data?.dispatchContainers || {};
        (this as any).totalCount = o.total || 0;
      },
    },
    drivers: {
      query: DriversQuery,
      skip() {
        return (this as any).showTripForContainerIdx === -1;
      },
    },
    chassis: {
      query: ChassisQuery,
      skip() {
        return (this as any).showTripForContainerIdx === -1;
      },
    },
  },
  methods: {
    setFilter(status: string) {
      const vm = this as any;

      if (vm.isListLoading) return;

      if (vm.activeFilter !== status) {
        vm.activeFilter = status;
        vm.pageNum = 0;
      }
    },
    toggleTripStatus(containerIdx: number) {
      const vm = this as any;

      if (vm.showTripForContainerIdx !== containerIdx) {
        vm.showTripForContainerIdx = containerIdx;
      } else {
        vm.showTripForContainerIdx = -1;
      }
    },
    closeDispatchContainer() {
      (this as any).showTripForContainerIdx = -1;
    },
    updateDispatchContainers() {
      (this as any).$apollo.queries.dispatchContainers.refetch();
    },
    updateSortBy(val: string) {
      const vm = this as any;

      if (vm.sortBy === val) {
        vm.sortOrder = vm.sortOrder === 'ASC' ? 'DESC' : 'ASC';
      } else {
        vm.sortBy = val;
        vm.sortOrder = 'ASC';
      }

      vm.pageNum = 0;
    },
    prevPage() {
      const vm = this as any;

      if (vm.isListLoading) return;

      if (vm.pageNum > 0) vm.pageNum -= 1;
    },
    nextPage() {
      const vm = this as any;

      if (vm.isListLoading) return;

      if (vm.pageNum + 1 >= vm.pageCount) return;
      vm.pageNum += 1;
    },
    goToPage(idx: number) {
      const vm = this as any;

      if (vm.isListLoading) return;
      if (idx === vm.pageNum) return;
      if (idx < 0 || idx >= vm.pageCount) return;
      vm.pageNum = idx;
    },
  },
});
</script>